import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useDialog } from '../../hooks/useDialog'
import { usePagination } from '../../hooks/usePagination'
import catalogService from '../../services/catalogService'
import { Season, SeasonPayload } from '../../types/catalog'
import { Button } from '../../components/ui/Button'
import { Dialog } from '../../components/ui/Dialog'
import { Pagination } from '../../components/ui/Pagination'
import { SeasonForm } from './SeasonForm'
import {
  PageHeader,
  PageMeta,
  PageEyebrow,
  PageTitle,
  TableWrapper,
  StyledTable,
  THead,
  TH,
  EmptyState,
  TableSkeleton,
} from '../../components/ui/Table'
import { useConfirm } from '../../hooks/useConfirm'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { SeasonRow } from './SeasonRow'

export function SeasonsPage() {
  const { token } = useAuth()
  const { page, perPage, setPage } = usePagination()
  const dialog = useDialog<Season>()

  const [items, setItems] = useState<Season[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [payload, setPayload] = useState<SeasonPayload | null>(null)

  const svc = useMemo(() => catalogService(token), [token])
  const { seasons } = svc

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await seasons.list(page, perPage)
      setItems(res.items)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [page, perPage, seasons])

  useEffect(() => {
    load()
  }, [load])

  const handleSave = useCallback(async () => {
    if (!payload) return
    setSaving(true)
    try {
      if (dialog.data) {
        await svc.seasons.update(dialog.data.id, payload)
      } else {
        await svc.seasons.create(payload)
      }
      dialog.close()
      load()
    } finally {
      setSaving(false)
    }
  }, [dialog, payload, svc, load])

  const confirmDialog = useConfirm()

  const handleDelete = useCallback(
    (item: Season) => {
      confirmDialog.confirm(
        'Delete season',
        `Are you sure you want to delete "${item.translations.find(t => t.locale === 'en')?.name ?? item.code}"? This action cannot be undone.`,
        async () => {
          await svc.seasons.delete(item.id)
          load()
        }
      )
    },
    [confirmDialog, svc, load]
  )

  return (
    <>
      <PageHeader>
        <PageMeta>
          <PageEyebrow>Catalog</PageEyebrow>
          <PageTitle>Seasons</PageTitle>
        </PageMeta>
        <Button onClick={() => dialog.open()}>+ Add season</Button>
      </PageHeader>

      {loading ? (
        <TableSkeleton cols={5} />
      ) : (
        <>
          <TableWrapper>
            <StyledTable>
              <THead>
                <tr>
                  <TH>Code</TH>
                  <TH>English</TH>
                  <TH>Spanish</TH>
                  <TH>Period</TH>
                  <TH>Position</TH>
                  <TH />
                </tr>
              </THead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState message="No seasons yet." />
                    </td>
                  </tr>
                )}
                {items.map(item => (
                  <SeasonRow
                    key={item.id}
                    item={item}
                    onEdit={dialog.open}
                    onDelete={handleDelete}
                  />
                ))}
              </tbody>
            </StyledTable>
          </TableWrapper>
          <Pagination page={page} total={total} perPage={perPage} onChange={setPage} />
        </>
      )}

      <Dialog
        open={dialog.isOpen}
        title={dialog.data ? 'Edit season' : 'Add season'}
        onClose={dialog.close}
        onConfirm={handleSave}
        confirmLabel={dialog.data ? 'Save changes' : 'Add season'}
        confirmLoading={saving}
        confirmDisabled={!payload?.code?.trim()}
      >
        <SeasonForm initial={dialog.data} onChange={setPayload} />
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.state.open}
        title={confirmDialog.state.title}
        message={confirmDialog.state.message}
        confirmLabel="Delete"
        onConfirm={confirmDialog.state.onConfirm}
        onCancel={confirmDialog.cancel}
        loading={confirmDialog.loading}
        variant="danger"
      />
    </>
  )
}
